import { useEffect, useState, useCallback } from "react";
import axiosInstance from "services/httpInterceptor";

export const usePieChartData = (companyId, platform, chartType, presetKey) => {

  const [pieChartData, setPieChartData] = useState([]);
  const [labels, setLabels] = useState([]);
  const [otherSliceDetails, setOtherSliceDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!companyId || !platform || !chartType || !presetKey) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {

      if (platform !== "linkedin" && platform !== "x") {
        throw new Error(`Platform ${platform} is not supported for distribution charts`);
      }

      let endpoint;
      if (chartType === "postType") {
        endpoint = `/v1/frontend/${platform}/distribution/postType`;
      } else if (chartType === "writingTone") {
        endpoint = `/v1/frontend/${platform}/distribution/writingTone`;
      } else {
        throw new Error(`Chart type ${chartType} is not supported`);
      }

      const response = await axiosInstance.get(endpoint, {
        params: { companyId, preset: presetKey.toUpperCase() },
      });

      if (response.data) {
        const { labels: originalLabels, percentages: originalPercentages } = response.data;

        if (originalLabels && originalPercentages && originalLabels.length === originalPercentages.length) {
          const totalSum = originalPercentages.reduce((sum, current) => sum + current, 0);
          const threshold = 0.03 * totalSum; // 3% threshold

          let aggregatedOtherValue = 0;
          const currentOtherSliceDetails = [];
          const finalLabels = [];
          const finalPercentages = [];

          // Pass 1: Identify large categories and sum up everything that will be "Other"
          originalLabels.forEach((label, index) => {
            const value = originalPercentages[index];
            if (value >= threshold && label.toLowerCase() !== "other") {
              finalLabels.push(label);
              finalPercentages.push(value);
            } else {
              // This slice will be part of "Other"
              aggregatedOtherValue += value;
              // Only add to details list if it wasn't originally "Other"
              if (label.toLowerCase() !== "other") {
                currentOtherSliceDetails.push({ label: label, value: value });
              }
            }
          });

          // Add the single aggregated "Other" slice if it has a value
          if (aggregatedOtherValue > 0) {
            finalLabels.push("Other");
            finalPercentages.push(aggregatedOtherValue);
          }

          // Store details for the "Other" slice
          const indexOfOtherInFinal = finalLabels.findIndex(l => l.toLowerCase() === "other");
          if (indexOfOtherInFinal !== -1 && currentOtherSliceDetails.length > 0) {
            currentOtherSliceDetails.sort((a, b) => b.value - a.value); // Sort details
            const detailsMap = new Map();
            detailsMap.set(indexOfOtherInFinal, currentOtherSliceDetails);
            setOtherSliceDetails(detailsMap);
          } else {
            setOtherSliceDetails(null);
          }

          // If all data was aggregated into "Other" and finalLabels is empty
          if (finalLabels.length === 0 && aggregatedOtherValue > 0) {
            // This case is covered by the above logic that pushes "Other" if aggregatedOtherValue > 0
          }

          setPieChartData(finalPercentages);
          setLabels(finalLabels);
        } else {
          // Fallback if data is not as expected
          setPieChartData(originalPercentages || []);
          setLabels(originalLabels || []);
          setOtherSliceDetails(null);
        }
      } else {
        // Handle case where response.data is null or undefined
        setPieChartData([]);
        setLabels([]);
        setOtherSliceDetails(null);
      }
    } catch (err) {
      console.error(`Error fetching ${chartType} distribution for ${platform}:`, err);
      setError(err.message);
      // Reset data on error
      setPieChartData([]);
      setLabels([]);
      setOtherSliceDetails(null);
    } finally {
      setLoading(false);
    }
  }, [companyId, platform, chartType, presetKey]);


  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { pieChartData, labels, otherSliceDetails, loading, error };
}; 